import * as Yup from "yup";
import YupPassword from "yup-password";
import { FieldMetaData, Fields } from "../types/SchemaFactory";
import { TFunction } from "i18next";
import { SelectProps } from "../types/SelectProps";
import _ from "lodash";
import {
    getCurrentDatetimeWithCorrectionMin,
    getCurrentDatetimeWithCorrectionYears,
} from "../tools/DatetimeTools";
import { workStartFromOffsetMin } from "../Config";

YupPassword(Yup);

export function getMetaParameterValue(
    data: FieldMetaData,
    key: "required",
): boolean;
export function getMetaParameterValue(
    data: FieldMetaData,
    key: "min" | "max",
): number | string;
export function getMetaParameterValue(
    data: FieldMetaData,
    key: "min" | "max" | "required",
) {
    const raw = data[key];
    const resolved = typeof raw === "function" ? raw() : raw;

    return _.get(resolved, "value", resolved);
}

export function getMetaParameterKey(
    data: FieldMetaData,
    key: "min" | "max" | "required",
    defaultValue: string,
) {
    const raw = data[key];
    const resolved = typeof raw === "function" ? raw() : raw;

    return _.get(resolved, "key", defaultValue);
}

export function schemaFactory<T extends Object>(
    t: TFunction,
    fields: Fields<T>,
    interpolationKey = "value",
) {
    return Yup.object().shape(
        Object.entries(fields).reduce(
            (acc, [key, value]: [string, FieldMetaData]) => {
                let schema:
                    | Yup.StringSchema
                    | Yup.NumberSchema
                    | Yup.BooleanSchema
                    | Yup.MixedSchema<any>
                    | Yup.ArraySchema<any, any, any>
                    | Yup.ObjectSchema<any>
                    | Yup.DateSchema<any> = Yup.string();
                if (value.type === "number") {
                    schema = Yup.number();
                } else if (value.type === "select") {
                    schema = Yup.mixed().oneOf(
                        value.selectOptions?.map(
                            (option: SelectProps) => option.value,
                        ) ?? [],
                        t("errors.validation.invalidOption"),
                    );
                } else if (value.type === "boolean") {
                    schema = Yup.boolean();
                } else if (value.type === "email") {
                    schema = Yup.string().email(
                        t("errors.validation.invalidEmail"),
                    );
                } else if (value.type === "password") {
                    schema = Yup.string()
                        .password()
                        .minSymbols(
                            1,
                            t("errors.validation.password.minSymbols", {
                                [interpolationKey]: 1,
                            }),
                        )
                        .minLowercase(
                            2,
                            t("errors.validation.password.minLowercase", {
                                [interpolationKey]: 2,
                            }),
                        )
                        .minUppercase(
                            1,
                            t("errors.validation.password.minUppercase", {
                                [interpolationKey]: 1,
                            }),
                        )
                        .minNumbers(
                            1,
                            t("errors.validation.password.minNumbers", {
                                [interpolationKey]: 1,
                            }),
                        )
                        .test(
                            "no-spaces",
                            t("errors.validation.password.noSpaces"),
                            function (value) {
                                return !/\s/.test(value);
                            },
                        );
                } else if (value.type === "schema") {
                    schema = schemaFactory(t, value.schema);
                } else if (value.pattern) {
                    schema = Yup.string().matches(
                        value.pattern,
                        t(value.patternErrorKey),
                    );
                } else if (
                    value.type === "datetime-local" ||
                    value.type === "date"
                ) {
                    schema = Yup.date();
                }

                if (
                    schema instanceof Yup.NumberSchema ||
                    schema instanceof Yup.StringSchema ||
                    schema instanceof Yup.DateSchema
                ) {
                    const min = getMetaParameterValue(value, "min") as number;
                    const max = getMetaParameterValue(value, "max") as number;

                    if (min && !isNaN(min) && min !== -Infinity) {
                        schema = schema.min(
                            min,
                            t(
                                getMetaParameterKey(
                                    value,
                                    "min",
                                    "errors.validation.min",
                                ),
                                {
                                    [interpolationKey]: min,
                                },
                            ),
                        );
                        if (schema instanceof Yup.DateSchema) {
                            schema = schema.test(
                                "start-from-min",
                                t(
                                    getMetaParameterKey(
                                        value,
                                        "min",
                                        "errors.validation.min",
                                    ),
                                    { [interpolationKey]: min },
                                ),
                                (value) => {
                                    if (!value) return true;
                                    const selected = new Date(value).getTime();
                                    const minAllowed = new Date(
                                        getCurrentDatetimeWithCorrectionMin(
                                            -workStartFromOffsetMin,
                                        ),
                                    ).getTime();
                                    return selected >= minAllowed;
                                },
                            );
                        }
                    }

                    if (max && !isNaN(max) && max !== Infinity) {
                        schema = (schema as Yup.StringSchema).max(
                            max,
                            t(
                                getMetaParameterKey(
                                    value,
                                    "max",
                                    "errors.validation.max",
                                ),
                                {
                                    [interpolationKey]: max,
                                },
                            ),
                        );
                        if (schema instanceof Yup.DateSchema) {
                            schema = schema.test(
                                "start-from-max",
                                t(
                                    getMetaParameterKey(
                                        value,
                                        "max",
                                        "errors.validation.max",
                                    ),
                                    { [interpolationKey]: max },
                                ),
                                (value) => {
                                    if (!value) return true;
                                    const selected = new Date(value).getTime();
                                    const maxAllowed = new Date(
                                        getCurrentDatetimeWithCorrectionYears(
                                            1,
                                        ),
                                    ).getTime();
                                    return selected <= maxAllowed;
                                },
                            );
                        }
                    }
                }

                schema = getMetaParameterValue(value, "required")
                    ? schema.required(
                          t(
                              getMetaParameterKey(
                                  value,
                                  "required",
                                  "errors.validation.required",
                              ),
                          ),
                      )
                    : schema.optional().nullable();

                if (value.array) {
                    schema = Yup.array()
                        .of(schema)
                        .min(
                            Number(getMetaParameterValue(value, "min")),
                            t(
                                getMetaParameterKey(
                                    value,
                                    "min",
                                    "errors.validation.min",
                                ),
                                {
                                    [interpolationKey]: getMetaParameterValue(
                                        value,
                                        "min",
                                    ),
                                },
                            ),
                        )
                        .max(
                            Number(getMetaParameterValue(value, "max")),
                            t(
                                getMetaParameterKey(
                                    value,
                                    "max",
                                    "errors.validation.max",
                                ),
                                {
                                    [interpolationKey]: getMetaParameterValue(
                                        value,
                                        "max",
                                    ),
                                },
                            ),
                        );
                }

                acc[key] = schema;
                return acc;
            },
            {} as Yup.ObjectShape,
        ),
    );
}
